import { Link, useLocation } from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";
import { useState, useEffect, useRef } from "react";

function CheckLocationPerms() {
  const location = useLocation();
  const ref = useRef(null);

  const openPermissionModal = () => {
    if (ref.current) ref.current.showModal();
  };

  const closePermissionModal = () => {
    if (ref.current) ref.current.close();
  };

  const requestLocationPermission = () => {
    if (!navigator.geolocation) {
      openPermissionModal();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      () => {
        closePermissionModal();
      },
      () => {
        openPermissionModal();
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  };

  useEffect(() => {
    let isCurrent = true;

    const checkPermissionState = async () => {
      if (!navigator.geolocation) {
        if (isCurrent) openPermissionModal();
        return;
      }

      try {
        const permission =
          navigator.permissions &&
          (await navigator.permissions.query({ name: "geolocation" }));

        if (!isCurrent) return;

        if (permission && permission.state === "denied") {
          openPermissionModal();
        }
      } catch (error) {
        // Ignore unsupported permission APIs; prompt remains available manually.
      }
    };

    checkPermissionState();

    return () => {
      isCurrent = false;
    };
  }, [location.pathname]);

  return (
    <>
      {location.pathname === "/auth" && (
        <div className="mt-6 flex justify-end">
          <button
            className="btn btn-primary btn-sm"
            onClick={requestLocationPermission}
          >
            Enable location access
          </button>
        </div>
      )}

      <dialog id="my_modal_3" className="modal" ref={ref}>
        <div className="modal-box h-full">
          <form method="dialog">
            <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">
              ✕
            </button>
          </form>
          <ShieldAlert />
          <h3 className="py-4 text-2xl">Location Permission issue</h3>
          <h4>Location is required to log a visit</h4>

          <p className="text-l py-4">
            This is likely an issue with either your browser or device settings.
          </p>
          <p className="py-4">
            Please use the instructions below for your device, or try enabling
            location again.
          </p>
          <div className="flex gap-2 flex-col">
            <button className="btn btn-primary" onClick={requestLocationPermission}>
              Enable location access again
            </button>
            <Link
              className="btn"
              target="_blank"
              to="https://support.google.com/chrome/answer/142065?hl=en-GB&co=GENIE.Platform%3DDesktop&oco=1"
            >
              Chrome (windows, mac & Android)
            </Link>
            <Link
              className="btn"
              target="_blank"
              to="https://support.apple.com/en-gb/guide/personal-safety/ips9bf20ad2f/web"
            >
              Safari (iOS & mac)
            </Link>
            <Link
              className="btn"
              target="_blank"
              to="https://support.microsoft.com/en-gb/microsoft-edge/location-and-privacy-in-microsoft-edge-31b5d154-0b1b-90ef-e389-7c7d4ffe7b04"
            >
              Microsoft Edge
            </Link>
          </div>
        </div>
      </dialog>
    </>
  );
}

export default CheckLocationPerms;
